import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { isImportJobFinished } from '../../models/masterImport.types';
import type { MasterImportJob } from '../../models/masterImport.types';

/** How often a running import is asked how it is getting on. */
const POLL_INTERVAL_MS = 1200;

/** One import the tray is following, and how to ask after it. */
export interface TrackedImport {
  job: MasterImportJob;
  /** The file's extension, for the tray's "Product · .xlsx" line. */
  format: string;
  /** Reads the job again. Belongs to the master that started it, so the tray needs no registry. */
  poll: (jobId: number) => Promise<MasterImportJob>;
  /**
   * Downloads the rows this import refused, as a file.
   *
   * <p>Carried on the job rather than looked up, for the same reason {@link #poll} is: the tray follows
   * imports from whichever master started them and holds no registry of who can do what.
   */
  downloadErrors?: (jobId: number) => Promise<void>;
  /**
   * Called once, when the job stops running.
   *
   * <p>How the master's table learns that rows it does not have are now there. The dialog used to do this
   * from its own poller — but it hands the job over and closes, so its poller never runs, and the import
   * finished with the table still showing what it had loaded before the upload. The screen had to be
   * refreshed by hand to see anything, which is the one thing a background import should not cost.
   */
  onFinished?: (job: MasterImportJob) => void;
}

interface ImportProgressValue {
  tracked: TrackedImport[];
  /** Follows a newly queued import. The dialog calls this and then closes. */
  track: (entry: TrackedImport) => void;
  /** Forgets every finished import; what the tray's close does once nothing is running. */
  clearFinished: () => void;
  /** Whether the tray is on screen at all. */
  visible: boolean;
  hide: () => void;
}

const ImportProgressContext = createContext<ImportProgressValue | null>(null);

/**
 * The imports this session is watching.
 *
 * <h2>Why it lives above the screen</h2>
 *
 * <p>A queued import outlives the dialog that started it, and the point of queuing it is that the uploader
 * can walk away. If the progress lived in the Import dialog it would die with the dialog, and closing the
 * thing that says "you can close this" would be the one action that loses the thread.
 *
 * <p>So the tray is mounted once, at the layout, and fed from here. Navigating between masters keeps it;
 * only a full reload drops it, and the import itself carries on regardless — the notification is what
 * reaches someone who has gone.
 */
export function ImportProgressProvider({ children }: { children: ReactNode }) {
  const [tracked, setTracked] = useState<TrackedImport[]>([]);
  const [visible, setVisible] = useState(false);
  /* Read by the one interval, so adding a job does not restart the timer. */
  const trackedRef = useRef<TrackedImport[]>([]);
  trackedRef.current = tracked;

  const track = useCallback((entry: TrackedImport) => {
    setTracked((current) => [...current.filter((t) => t.job.jobId !== entry.job.jobId), entry]);
    setVisible(true);
  }, []);

  const clearFinished = useCallback(() => {
    setTracked((current) => current.filter((t) => !isImportJobFinished(t.job)));
  }, []);

  const hide = useCallback(() => {
    setVisible(false);
  }, []);

  /*
    One timer for every running import rather than one each: the tray shows them together, and a timer per
    job would poll n times per tick for a list the user reads as a whole.
  */
  useEffect(() => {
    const running = tracked.filter((t) => !isImportJobFinished(t.job));
    if (running.length === 0) {
      return;
    }
    let cancelled = false;
    const timer = window.setInterval(() => {
      const live = trackedRef.current.filter((t) => !isImportJobFinished(t.job));
      live.forEach((entry) => {
        void entry
          .poll(entry.job.jobId)
          .then((next) => {
            if (cancelled) return;
            setTracked((current) =>
              current.map((t) => (t.job.jobId === next.jobId ? { ...t, job: next } : t)),
            );
            /*
              Announced on the transition, not on every poll that finds it finished: the timer keeps
              running until the last job stops, and a table that reloaded on each tick would reload
              forever. `entry.job` is this tick's starting state, so it is still the running one here.
            */
            if (isImportJobFinished(next) && !isImportJobFinished(entry.job)) {
              entry.onFinished?.(next);
            }
          })
          .catch(() => {
            /*
              A failed poll is not a failed import: the job is still running on the server and all that is
              lost is this tray's view of it. The next tick asks again.
            */
          });
      });
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
    /* Keyed on how many are running, so the timer starts with the first and stops after the last. */
  }, [tracked]);

  const value = useMemo<ImportProgressValue>(
    () => ({ tracked, track, clearFinished, visible, hide }),
    [tracked, track, clearFinished, visible, hide],
  );

  return <ImportProgressContext.Provider value={value}>{children}</ImportProgressContext.Provider>;
}

/**
 * The tray's state, for a component inside the provider.
 *
 * <p>Returns null outside one rather than throwing: a master screen rendered in a test harness without the
 * layout should still work, and an import that cannot be tracked falls back to reporting in the dialog.
 */
export function useImportProgress(): ImportProgressValue | null {
  return useContext(ImportProgressContext);
}
